import React, { useState, useMemo } from 'react';
import { orgApi } from '../utils/apiV2';
import { Button } from './ui/button';
import { 
  Sparkles, 
  Check, 
  AlertCircle, 
  Loader2,
  ChevronDown,
  ChevronRight,
  Zap,
  Calendar,
  Users,
  Package,
  RefreshCw,
  Clock,
  Phone,
  AlertTriangle,
  HelpCircle,
  XCircle,
  CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarPicker } from './ui/calendar';
import { format, addDays, startOfWeek, endOfWeek, isWithinInterval, parseISO } from 'date-fns';

/**
 * SmartSchedulePanel v2 - Optimized bulk AI scheduling
 * 
 * Features:
 * - Date picker with presets (Today, Tomorrow, This Week, Next 7 Days, Custom)
 * - Shows breakdown of what's needed (assets vs team vs both)
 * - Rate limiting display
 * - Actionable suggestions (not just assignments)
 * - Alternatives when resources unavailable
 */
const SmartSchedulePanel = ({ 
  bookings = [], 
  assets = [], 
  teamMembers = [],
  onScheduleApplied,
  isOpen,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState({});
  const [selectedBookings, setSelectedBookings] = useState({});
  const [applyingAll, setApplyingAll] = useState(false);
  const [expandedBookings, setExpandedBookings] = useState({});
  const [rateLimit, setRateLimit] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [showPreview, setShowPreview] = useState(true); // Show booking preview by default
  
  // Date filter state
  const [datePreset, setDatePreset] = useState('next7'); // today, tomorrow, thisWeek, next7, custom
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Calculate date range based on preset
  const getDateRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (datePreset) {
      case 'today':
        return { from: today, to: today };
      case 'tomorrow':
        const tomorrow = addDays(today, 1);
        return { from: tomorrow, to: tomorrow };
      case 'thisWeek':
        return { from: startOfWeek(today, { weekStartsOn: 0 }), to: endOfWeek(today, { weekStartsOn: 0 }) };
      case 'next7':
        return { from: today, to: addDays(today, 6) };
      case 'custom':
        return customDateRange.from && customDateRange.to ? customDateRange : { from: today, to: addDays(today, 6) };
      default:
        return { from: today, to: addDays(today, 6) };
    }
  };
  
  const dateRange = getDateRange();
  
  // Filter bookings by date range
  const filteredBookings = useMemo(() => {
    if (!dateRange.from || !dateRange.to) return bookings;
    
    return bookings.filter(b => {
      if (!b.date) return false;
      try {
        const bookingDate = parseISO(b.date);
        return isWithinInterval(bookingDate, { start: dateRange.from, end: dateRange.to });
      } catch {
        return false;
      }
    });
  }, [bookings, dateRange.from, dateRange.to]);
  
  // Get preset label for display
  const getPresetLabel = () => {
    switch (datePreset) {
      case 'today': return 'Today';
      case 'tomorrow': return 'Tomorrow';
      case 'thisWeek': return 'This Week';
      case 'next7': return 'Next 7 Days';
      case 'custom': 
        if (customDateRange.from && customDateRange.to) {
          return `${format(customDateRange.from, 'MMM d')} - ${format(customDateRange.to, 'MMM d')}`;
        }
        return 'Custom Range';
      default: return 'Next 7 Days';
    }
  };
  
  // Analyze bookings to show what's needed
  const bookingAnalysis = useMemo(() => {
    const needsAsset = [];
    const needsTeam = [];
    const needsBoth = [];
    const fullyScheduled = [];
    
    filteredBookings.forEach(b => {
      const hasAsset = b.assetId && b.assetId !== 'unassigned';
      const hasTeam = b.teamMemberId || (b.teamAssignments?.some(a => a.teamId));
      
      if (!hasAsset && !hasTeam) {
        needsBoth.push(b);
      } else if (!hasAsset) {
        needsAsset.push(b);
      } else if (!hasTeam) {
        needsTeam.push(b);
      } else {
        fullyScheduled.push(b);
      }
    });
    
    return { needsAsset, needsTeam, needsBoth, fullyScheduled };
  }, [filteredBookings]);
  
  const totalUnscheduled = bookingAnalysis.needsAsset.length + 
                           bookingAnalysis.needsTeam.length + 
                           bookingAnalysis.needsBoth.length;
  
  const handleGenerateSuggestions = async () => {
    const unscheduledIds = [
      ...bookingAnalysis.needsAsset,
      ...bookingAnalysis.needsTeam,
      ...bookingAnalysis.needsBoth
    ].map(b => b.id);
    
    if (unscheduledIds.length === 0) {
      toast.info('All visible bookings are already scheduled!');
      return;
    }
    
    setLoading(true);
    setSuggestions({});
    setSelectedBookings({});
    setAiSummary(null);
    
    try {
      const response = await orgApi.getBulkScheduleSuggestions(unscheduledIds);
      
      if (response.data?.success) {
        setSuggestions(response.data.suggestions);
        setRateLimit(response.data.rate_limit);
        setAiSummary(response.data.summary);
        
        // Auto-select "can_assign" with high confidence
        const autoSelected = {};
        Object.entries(response.data.suggestions).forEach(([bookingId, suggestion]) => {
          if (suggestion.success && 
              suggestion.status === 'can_assign' &&
              suggestion.suggestion?.recommendation?.confidence === 'high') {
            autoSelected[bookingId] = true;
          }
        });
        setSelectedBookings(autoSelected);
        
        const summary = response.data.summary;
        toast.success(
          `AI analyzed ${summary.total} bookings: ${summary.can_assign} assignable, ${summary.needs_action} need action`
        );
      } else {
        toast.error(response.data?.error || 'Failed to generate suggestions');
        if (response.data?.rate_limit) {
          setRateLimit(response.data.rate_limit);
        }
      }
    } catch (error) {
      console.error('Error generating suggestions:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate suggestions');
    } finally {
      setLoading(false);
    }
  };
  
  const handleApplySelected = async () => {
    const selectedIds = Object.keys(selectedBookings).filter(id => selectedBookings[id]);
    
    if (selectedIds.length === 0) {
      toast.warning('Select at least one booking to apply');
      return;
    }
    
    setApplyingAll(true);
    
    try {
      const assignments = selectedIds.map(bookingId => {
        const suggestion = suggestions[bookingId]?.suggestion?.recommendation;
        return {
          booking_id: bookingId,
          asset_id: suggestion?.asset_id || null,
          team_member_id: suggestion?.team_member_id || null
        };
      });
      
      const response = await orgApi.applyBulkSchedule(assignments);
      
      if (response.data?.success) {
        toast.success(`Applied ${response.data.summary.successful} assignments!`);
        
        const remaining = { ...suggestions };
        selectedIds.forEach(id => delete remaining[id]);
        setSuggestions(remaining);
        setSelectedBookings({});
        
        if (onScheduleApplied) onScheduleApplied();
      } else {
        toast.error('Failed to apply some suggestions');
      }
    } catch (error) {
      console.error('Error applying suggestions:', error);
      toast.error(error.response?.data?.detail || 'Failed to apply suggestions');
    } finally {
      setApplyingAll(false);
    }
  };
  
  const toggleBooking = (bookingId) => {
    setSelectedBookings(prev => ({ ...prev, [bookingId]: !prev[bookingId] }));
  };
  
  const toggleSelectAll = () => {
    const assignableIds = Object.keys(suggestions).filter(
      id => suggestions[id]?.success && suggestions[id]?.status === 'can_assign'
    );
    const allSelected = assignableIds.every(id => selectedBookings[id]);
    
    if (allSelected) {
      setSelectedBookings({});
    } else {
      const newSelected = {};
      assignableIds.forEach(id => { newSelected[id] = true; });
      setSelectedBookings(newSelected);
    }
  };
  
  const toggleExpanded = (bookingId) => {
    setExpandedBookings(prev => ({ ...prev, [bookingId]: !prev[bookingId] }));
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'can_assign':
        return <Check className="w-4 h-4 text-green-600" />;
      case 'needs_action':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'impossible':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <HelpCircle className="w-4 h-4 text-gray-400" />;
    }
  };
  
  const getConfidenceBadge = (confidence) => {
    const styles = {
      high: 'bg-green-50 text-green-700 border-green-200',
      medium: 'bg-amber-50 text-amber-700 border-amber-200',
      low: 'bg-gray-50 text-gray-600 border-gray-200'
    };
    return (
      <span className={`text-xs px-1.5 py-0.5 rounded border ${styles[confidence] || styles.low}`}>
        {confidence}
      </span>
    );
  };
  
  const getBookingDetails = (bookingId) => filteredBookings.find(b => b.id === bookingId);
  
  const selectedCount = Object.values(selectedBookings).filter(Boolean).length;
  const hasSuggestions = Object.keys(suggestions).length > 0;
  
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Smart Schedule
              {rateLimit && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <span className="text-xs font-normal text-gray-400 ml-2">
                        ({rateLimit.remaining}/{rateLimit.max_per_hour} calls left)
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Rate limit resets in {rateLimit.reset_in_minutes} minutes</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {/* Date Filter Presets */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <CalendarDays className="w-4 h-4 text-gray-400" />
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {[
                { key: 'today', label: 'Today' },
                { key: 'tomorrow', label: 'Tomorrow' },
                { key: 'thisWeek', label: 'This Week' },
                { key: 'next7', label: 'Next 7 Days' },
              ].map(preset => (
                <button
                  key={preset.key}
                  onClick={() => {
                    setDatePreset(preset.key);
                    setSuggestions({});
                    setSelectedBookings({});
                  }}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                    datePreset === preset.key
                      ? 'bg-white text-purple-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
              
              {/* Custom Date Picker */}
              <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                <PopoverTrigger asChild>
                  <button
                    onClick={() => setDatePreset('custom')}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                      datePreset === 'custom'
                        ? 'bg-white text-purple-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {datePreset === 'custom' && customDateRange.from ? getPresetLabel() : 'Custom'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <CalendarPicker
                    mode="range"
                    selected={customDateRange}
                    onSelect={(range) => {
                      setCustomDateRange(range || { from: null, to: null });
                      if (range?.from && range?.to) {
                        setDatePreset('custom');
                        setSuggestions({});
                        setSelectedBookings({});
                        setShowDatePicker(false);
                      }
                    }}
                    numberOfMonths={1}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Summary Card with Breakdown */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 mb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {getPresetLabel()}
                </p>
                <p className="text-sm text-gray-700 mb-2">
                  {totalUnscheduled} bookings need scheduling
                </p>
                {/* Breakdown badges */}
                <div className="flex flex-wrap gap-2 text-xs">
                  {bookingAnalysis.needsAsset.length > 0 && (
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {bookingAnalysis.needsAsset.length} need asset
                    </span>
                  )}
                  {bookingAnalysis.needsTeam.length > 0 && (
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {bookingAnalysis.needsTeam.length} need team
                    </span>
                  )}
                  {bookingAnalysis.needsBoth.length > 0 && (
                    <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {bookingAnalysis.needsBoth.length} need both
                    </span>
                  )}
                </div>
              </div>
              <Button
                onClick={handleGenerateSuggestions}
                disabled={loading || totalUnscheduled === 0}
                className="bg-purple-600 hover:bg-purple-700 text-white shrink-0"
                size="sm"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    Analyzing...
                  </>
                ) : hasSuggestions ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1.5" />
                    Regenerate
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-1.5" />
                    Generate
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {/* Booking Preview - Show BEFORE generating suggestions */}
          {!hasSuggestions && totalUnscheduled > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 mb-2"
              >
                {showPreview ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                Preview bookings to schedule
              </button>
              
              {showPreview && (
                <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
                  {[...bookingAnalysis.needsBoth, ...bookingAnalysis.needsAsset, ...bookingAnalysis.needsTeam].map((booking) => {
                    const needsAsset = !booking.assetId || booking.assetId === 'unassigned';
                    const needsTeam = !booking.teamMemberId && !booking.teamAssignments?.some(a => a.teamId);
                    
                    return (
                      <div key={booking.id} className="px-3 py-2 flex items-center justify-between gap-3 text-sm hover:bg-gray-50">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-mono text-xs text-gray-500">{booking.confirmationCode}</span>
                          <span className="text-gray-900 truncate">{booking.customerName || 'Unknown'}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-gray-500">{formatDateDisplay(booking.date)}</span>
                          <span className="text-xs text-gray-500">{booking.startTime?.slice(0, 5)}</span>
                          <span className="text-xs text-gray-500">{booking.partySize || 1}p</span>
                          {/* Status indicators */}
                          <div className="flex items-center gap-1">
                            {needsAsset && (
                              <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center" title="Needs asset">
                                <Package className="w-3 h-3" />
                              </span>
                            )}
                            {needsTeam && (
                              <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center" title="Needs team">
                                <Users className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {/* AI Summary */}
          {aiSummary && (
            <div className="bg-white border rounded-lg p-3 mb-4 text-sm">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1 text-green-700">
                  <Check className="w-4 h-4" />
                  {aiSummary.can_assign} can assign
                </span>
                <span className="flex items-center gap-1 text-amber-600">
                  <AlertTriangle className="w-4 h-4" />
                  {aiSummary.needs_action} need action
                </span>
                {aiSummary.impossible > 0 && (
                  <span className="flex items-center gap-1 text-red-600">
                    <XCircle className="w-4 h-4" />
                    {aiSummary.impossible} impossible
                  </span>
                )}
              </div>
              {aiSummary.unscheduled_team?.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  💡 Unscheduled team today: {aiSummary.unscheduled_team.join(', ')}
                </p>
              )}
            </div>
          )}
          
          {/* Empty State */}
          {totalUnscheduled === 0 && !hasSuggestions && (
            <div className="text-center py-12">
              <Check className="w-10 h-10 mx-auto text-green-500 mb-2" />
              <p className="text-gray-900 font-medium">All set!</p>
              <p className="text-sm text-gray-500 mt-1">All visible bookings are scheduled.</p>
            </div>
          )}
          
          {/* Suggestions List */}
          {hasSuggestions && (
            <div className="space-y-2">
              {/* Select All Bar */}
              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg sticky top-0 z-10">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox
                    checked={Object.keys(suggestions).filter(id => suggestions[id]?.status === 'can_assign').every(id => selectedBookings[id])}
                    onCheckedChange={toggleSelectAll}
                  />
                  <span className="text-gray-600">
                    {selectedCount > 0 ? `${selectedCount} selected` : 'Select assignable'}
                  </span>
                </label>
                <Button
                  onClick={handleApplySelected}
                  disabled={selectedCount === 0 || applyingAll}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  size="sm"
                >
                  {applyingAll ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Apply ({selectedCount})
                    </>
                  )}
                </Button>
              </div>
              
              {/* Suggestion Cards */}
              {Object.entries(suggestions).map(([bookingId, suggestion]) => {
                const booking = getBookingDetails(bookingId);
                const isExpanded = expandedBookings[bookingId];
                const isSelected = selectedBookings[bookingId];
                const rec = suggestion.suggestion?.recommendation;
                const canSelect = suggestion.status === 'can_assign';
                
                if (!booking) return null;
                
                return (
                  <div 
                    key={bookingId}
                    className={`border rounded-lg p-3 transition-all ${
                      isSelected ? 'border-purple-300 bg-purple-50/50' : 
                      suggestion.status === 'needs_action' ? 'border-amber-200 bg-amber-50/30' :
                      suggestion.status === 'impossible' ? 'border-red-200 bg-red-50/30' :
                      'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleBooking(bookingId)}
                        disabled={!canSelect}
                        className="mt-0.5"
                      />
                      
                      <div className="flex-1 min-w-0">
                        {/* Header Row */}
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <div className="flex items-center gap-2 min-w-0">
                            {getStatusIcon(suggestion.status)}
                            <span className="font-mono text-sm font-medium text-gray-900 truncate">
                              {suggestion.booking_code || booking.confirmationCode}
                            </span>
                            {rec?.confidence && getConfidenceBadge(rec.confidence)}
                          </div>
                          <button
                            onClick={() => toggleExpanded(bookingId)}
                            className="text-gray-400 hover:text-gray-600 p-1 -mr-1"
                          >
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </div>
                        
                        {/* Info Row */}
                        <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDateDisplay(booking.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {booking.startTime?.slice(0, 5)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {booking.partySize || booking.groupSize || 1}
                          </span>
                          {booking.customerName && (
                            <span className="truncate">{booking.customerName}</span>
                          )}
                        </div>
                        
                        {/* Suggestion Content */}
                        {suggestion.status === 'can_assign' && rec ? (
                          <div className="flex items-center gap-3 text-sm">
                            {rec.asset_name && (
                              <span className="flex items-center gap-1 text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                                <Package className="w-3.5 h-3.5" />
                                {rec.asset_name}
                              </span>
                            )}
                            {rec.team_member_name && (
                              <span className="flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded">
                                <Users className="w-3.5 h-3.5" />
                                {rec.team_member_name}
                              </span>
                            )}
                          </div>
                        ) : suggestion.status === 'needs_action' ? (
                          <div className="text-sm">
                            <p className="text-amber-700 font-medium mb-1">
                              {suggestion.suggestion?.action_needed || 'Action required'}
                            </p>
                            {suggestion.suggestion?.alternatives?.length > 0 && (
                              <div className="text-xs text-gray-600 space-y-0.5">
                                {suggestion.suggestion.alternatives.map((alt, idx) => (
                                  <p key={idx} className="flex items-start gap-1">
                                    <span className="text-amber-500">•</span>
                                    {alt}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : suggestion.status === 'impossible' ? (
                          <div className="text-sm text-red-600">
                            <p>{rec?.reason || 'Unable to schedule'}</p>
                            {suggestion.context?.customer_phone && (
                              <a 
                                href={`tel:${suggestion.context.customer_phone}`}
                                className="flex items-center gap-1 text-blue-600 hover:underline mt-1"
                              >
                                <Phone className="w-3 h-3" />
                                Contact customer: {suggestion.context.customer_phone}
                              </a>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-gray-500 text-xs">
                            <AlertCircle className="w-3.5 h-3.5" />
                            {suggestion.error || 'Could not analyze'}
                          </div>
                        )}
                        
                        {/* Expanded Details */}
                        {isExpanded && rec?.reason && (
                          <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600">
                            <p><span className="font-medium">Reason:</span> {rec.reason}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Loading Skeleton */}
          {loading && (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="border border-gray-100 rounded-lg p-3 animate-pulse">
                  <div className="flex items-start gap-3">
                    <div className="w-4 h-4 bg-gray-200 rounded mt-0.5" />
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
                      <div className="h-3 bg-gray-100 rounded w-48 mb-2" />
                      <div className="h-6 bg-gray-100 rounded w-32" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SmartSchedulePanel;
